import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, Truck, FileText, ArrowRight } from 'lucide-react';

const WelcomeStep = ({ data, onNext, isLoading, updateData }) => {
  const navigate = useNavigate();

  const nextSteps = [
    {
      icon: Users,
      title: 'Add Your First Driver',
      description: 'Start by adding driver information and uploading required documents like CDL and medical certificates.',
      action: 'Add Driver',
      route: '/drivers/new'
    },
    {
      icon: Truck,
      title: 'Register Your Vehicles',
      description: 'Add your fleet vehicles with registration, insurance, and inspection information.',
      action: 'Add Vehicle',
      route: '/vehicles/new'
    },
    {
      icon: FileText,
      title: 'Upload Documents',
      description: 'Ensure all compliance documents are uploaded and up-to-date for drivers and vehicles.',
      action: 'Manage Documents',
      route: '/documents'
    }
  ];

  const welcomeData = useMemo(() => data?.welcome || {}, [data?.welcome]);

  const recordAction = useCallback((action, context = {}) => {
    const timestamp = new Date().toISOString();
    const history = Array.isArray(welcomeData?.actionHistory)
      ? [...welcomeData.actionHistory]
      : [];

    history.push({ action, context, timestamp });

    updateData?.('welcome', {
      lastAction: action,
      lastActionAt: timestamp,
      lastActionContext: context,
      actionHistory: history
    });
  }, [updateData, welcomeData]);

  const handleStepClick = (step) => {
    recordAction('recommended_step', {
      route: step.route,
      title: step.title
    });
    navigate(step.route);
  };

  const handleDashboardClick = () => {
    recordAction('dashboard_entry', {
      source: 'welcome_cta'
    });

    if (typeof onNext === 'function') {
      onNext();
    }
  };

  const configurationSummary = [
    {
      label: 'Company Name',
      value: data.company?.name || 'Not set'
    },
    {
      label: 'DOT Number',
      value: data.company?.dotNumber || 'Not set'
    },
    {
      label: 'Fleet Size',
      value: data.company?.fleetSize ? `${data.company.fleetSize} vehicles` : 'Not specified'
    },
    {
      label: 'Subscription Plan',
      value: data.company?.subscriptionPlan?.charAt(0).toUpperCase() + data.company?.subscriptionPlan?.slice(1) || 'Basic'
    },
    {
      label: 'Administrator',
      value: data.adminUser ? `${data.adminUser.firstName} ${data.adminUser.lastName}` : 'Not set'
    },
    {
      label: 'Email Notifications',
      value: data.configuration?.reminderSettings?.emailNotifications ? 'Enabled' : 'Disabled'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Success Header */}
      <div className="text-center">
        <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900">
          Welcome to Himil Fleet!
        </h2>
        <p className="mt-2 text-lg text-gray-600">
          Your company has been successfully set up and configured.
        </p>
      </div>

      {/* Configuration Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Setup Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configurationSummary.map((item, index) => (
            <div key={index} className="flex justify-between py-2">
              <span className="text-sm font-medium text-gray-500">{item.label}:</span>
              <span className="text-sm text-gray-900">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Recommended Next Steps
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {nextSteps.map((step, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{step.title}</h4>
                  <p className="mt-1 text-sm text-gray-500">{step.description}</p>
                  <div className="mt-3">
                    <button
                      onClick={() => handleStepClick(step)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors duration-200"
                    >
                      {step.action} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>


      {/* Completion Button */}
      <div className="pt-4">
        <button
          onClick={handleDashboardClick}
          disabled={isLoading}
          className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Completing Setup...
            </>
          ) : (
            <>
              Get Started with Your Dashboard
              <ArrowRight className="h-5 w-5 ml-2" />
            </>
          )}
        </button>
      </div>

      {/* Additional Info */}
      <div className="text-center text-sm text-gray-500">
        <p>
          You can always access these settings later from the Settings page.
          <br />
          Your onboarding is complete and your account is ready to use!
        </p>
      </div>
    </div>
  );
};

export default WelcomeStep;
